// NavigatorInside — the Navigator hosted INSIDE a picker (ADR-080 §14): a FIELD
// through `Picker` (`navigator`), a FILTER through the kit's `FilterInside`
// (a dimension's `navigator`). The host of what the Navigator primitive leaves
// to its host — the open LEVEL, its path, the typed search — over the rows the
// family's hook answers (`useOptions`: a small source whose rows carry `inside`,
// the administrative areas; at rest the whole tree under the parent, typed the
// ranked matches across every level). Props in, events out, like `PickerList`:
// nothing is fetched or matched here. One level is listed at a time — the rows
// directly inside the open one, an arrow on a row holding rows, the back row
// one level up, the "this level" row with its fixed line (the consumer words
// it: "This area" in a form, "This area and its sub-areas" in a filter); a
// search answers flat, each match with its PATH on its second line (the row's
// own `path` — the names above it, top first). A pick at ANY level is a pick
// of that level: a form places the thing AT it, a filter means everything
// within it (the read expands). In a filter a row inside a picked ancestor
// reads "Included" and cannot be picked on its own — only the highest pick is
// stored (ADR-080 §8). The keys, Escape and the two targets are the Navigator's.

import { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { OptionRow, Options } from '../../hooks/useOptions.js';
import { Navigator, type NavigatorRow } from '../ui/Navigator.js';

const NOTICE = 'px-3 py-2 text-sm text-[var(--color-text-secondary)]';

/** The ids above a row, nearest first. */
function ancestorsOf(byId: ReadonlyMap<string, OptionRow>, id: string): string[] {
  const out: string[] = [];
  const seen = new Set<string>([id]);
  let cur = byId.get(id)?.inside ?? null;
  while (cur !== null && !seen.has(cur)) {
    out.push(cur);
    seen.add(cur);
    cur = byId.get(cur)?.inside ?? null;
  }
  return out;
}

/** Only the HIGHEST pick is kept: a value inside a picked ancestor leaves. */
export function highestInTree(byId: ReadonlyMap<string, OptionRow>, values: readonly string[]): string[] {
  const set = new Set(values);
  return [...set].filter((v) => !ancestorsOf(byId, v).some((a) => set.has(a)));
}

export interface NavigatorInsideProps {
  /** The hook's answer — the rows under the parent at rest (the whole tree), the matches once typed. */
  options: Options;
  text: string;
  onTextChange: (text: string) => void;
  value: readonly string[];
  onChange: (next: string[]) => void;
  multiple?: boolean;
  /** The fixed second line of the "this level" row. */
  thisLevelLine: string;
  searchPlaceholder?: string;
  /** Focus the search box on mount — a panel the person just opened; never in a drawer listing several. */
  searchAutoFocus?: boolean;
  /** After a single pick — the host closes its panel. */
  onPick?: () => void;
  ariaLabel: string;
  rowSize?: 'default' | 'touch';
}

export function NavigatorInside({ options, text, onTextChange, value, onChange, multiple = false, thisLevelLine, searchPlaceholder, searchAutoFocus = false, onPick, ariaLabel, rowSize = 'default' }: NavigatorInsideProps) {
  const { t } = useTranslation('common');
  const statusId = useId();
  const typed = text.trim().length > 0;
  // The open level (null = the top) — the host's; the path is walked from it each render.
  const [level, setLevel] = useState<string | null>(null);

  // The WHOLE tree by id (the hook's whole rows, whatever is typed): a match's ancestry (Included), the path and the
  // highest pick are walked on it. The rows AT REST — the hook's answer under the parent, the hidden inactive rows
  // left out — are what a level lists and what an arrow leads into.
  const all = options.whole ?? options.rows;
  const wholeById = useMemo(() => new Map(all.map((r) => [r.id, r] as const)), [all]);
  const rest = typed ? null : options.rows;
  const restById = useMemo(() => (rest ? new Map(rest.map((r) => [r.id, r] as const)) : null), [rest]);

  // The level must be a row the tree still offers (a parent change may have emptied it) — else back to the top.
  const open = level !== null && (restById ?? wholeById).has(level) ? level : null;
  const path = useMemo(() => {
    if (open === null) return [];
    const trail = ancestorsOf(wholeById, open).reverse().map((id) => ({ id, label: wholeById.get(id)!.label }));
    return [...trail, { id: open, label: wholeById.get(open)!.label }];
  }, [wholeById, open]);

  const picked = useMemo(() => new Set(value), [value]);
  const included = (id: string) => multiple && ancestorsOf(wholeById, id).some((a) => picked.has(a));
  const hasInside = (id: string) => {
    for (const r of restById?.values() ?? []) if (r.inside === id) return true;
    return false;
  };
  const includedWord = t('picker.included');
  const toRow = (r: OptionRow, inSearch: boolean): NavigatorRow => {
    const inc = included(r.id);
    // A match says where it sits (its path); a row inside a level needs no path — the back row says where we are.
    const path = inSearch ? (r.path ?? '') : '';
    return {
      id: r.id,
      label: r.label,
      description: inc ? [path, includedWord].filter(Boolean).join(' · ') : path || r.description,
      hasChildren: !inSearch && hasInside(r.id),
      pickable: r.pickable && !inc,
      inactive: r.inactive,
      included: inc,
    };
  };

  const levelRows: NavigatorRow[] = rest ? rest.filter((r) => (r.inside ?? null) === open).map((r) => toRow(r, false)) : [];
  const results: NavigatorRow[] = typed ? options.rows.map((r) => toRow(r, true)) : [];
  const here = open !== null ? wholeById.get(open) : undefined;
  // The level's own row cannot be picked inside a picked ancestor either — the rows say "Included", this one is left out.
  const thisLevel = here && !included(here.id) ? { id: here.id, label: here.label, line: thisLevelLine } : undefined;

  const onSelection = (next: string[]) => {
    const kept = multiple ? highestInTree(wholeById, next) : next.slice(0, 1);
    onChange(kept);
    if (!multiple) onPick?.();
  };

  // One word inside an empty list (the family's rule): the start list loading or failed; else the Navigator's own.
  const emptyWord = options.failed ? t('picker.failed') : options.loading ? t('picker.loading') : undefined;
  const listed = typed ? results.length : levelRows.length + (thisLevel ? 1 : 0);
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* A failure beside rows already on screen says so above them (an empty list says it inside). */}
      {options.failed && all.length > 0 && <p className={NOTICE}>{t('picker.failed')}</p>}
      <Navigator
        rows={levelRows}
        path={path}
        onEnter={(id) => setLevel(id)}
        onBack={() => setLevel(path.length > 1 ? path[path.length - 2]!.id : null)}
        value={value}
        onChange={onSelection}
        multiple={multiple}
        thisLevel={thisLevel}
        // The box shows on a searched list — and on a short one once text is typed (a letter typed on the closed field),
        // so the typed text is always seen and can be cleared.
        search={options.searchable || typed ? { text, onTextChange, results, placeholder: searchPlaceholder, autoFocus: searchAutoFocus } : undefined}
        ariaLabel={ariaLabel}
        describedBy={statusId}
        emptyWord={emptyWord}
        rowSize={rowSize}
        className="min-h-0 flex-1"
      />
      {/* The count, READ ALOUD ONLY — the family's polite status line the list is described by. */}
      <div id={statusId} role="status" aria-live="polite" className="sr-only">
        {options.failed ? t('picker.failed') : options.loading ? '' : t('picker.results', { count: listed })}
      </div>
    </div>
  );
}
